import { Location } from "@angular/common";
import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	OnDestroy,
	OnInit,
	TemplateRef,
	ViewChild,
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { isEmpty } from "lodash";
import moment from "moment";
import { Columns, Config, DefaultConfig, Pagination } from "ngx-easy-table";
import { Subscription } from "rxjs";
import { CONSTANT } from "src/app/constant";
import { RouteType } from "src/app/models/enum";
import { PaginationDetails, PilotData, PilotDataParams } from "src/app/models/models";
import { HttpService } from "src/app/shared/services/http.service";
import { LoaderService } from "src/app/shared/services/loader.service";
import { SharedService } from "src/app/shared/services/shared.service";

@Component({
	selector: "app-pilot-data",
	templateUrl: "./pilot-data.component.html",
	styleUrl: "./pilot-data.component.scss",
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class PilotDataComponent implements OnInit, OnDestroy, AfterViewInit {
	public columns!: Columns[];
	public configuration!: Config;
	selectedPilot?: PilotData;

	@ViewChild("idTpl", { static: true }) idTpl!: TemplateRef<any>;
	@ViewChild("nameTpl", { static: true }) nameTpl!: TemplateRef<any>;
	@ViewChild("uploadedDateTpl", { static: true }) uploadedDateTpl!: TemplateRef<any>;
	@ViewChild("actionTpl", { static: true }) actionTpl!: TemplateRef<any>;

	DATE_FORMAT = CONSTANT.DATE_FORMAT;

	filters?: PilotDataParams = {
		page: this.constant.PAGINATION.DEFAULT_PAGE,
		size: this.constant.PAGINATION.DEFAULT_SIZE,
	};

	public pagination: Pagination = {
		limit: this.constant.PAGINATION.DEFAULT_SIZE,
		offset: 1,
		count: 1,
	};

	searchQuery: string = "";
	selectedFilter: string = "";

	onlineStatusSub!: Subscription;
	pilotsSub!: Subscription;
	pilots: PilotData[] = [];
	routeSub!: Subscription;

	private searchTimeout: any;
	isOnline!: boolean;

	constructor(
		public sharedService: SharedService,
		public router: Router,
		private httpService: HttpService,
		private route: ActivatedRoute,
		private loaderService: LoaderService,
		private location: Location,
		private cdr: ChangeDetectorRef,
	) {}

	ngOnInit(): void {
		this.sharedService.setActivePage(RouteType.PILOT_DATA);

		this.onlineStatusSub = this.sharedService.getIsStatusOnline().subscribe((status: boolean) => {
			this.isOnline = status;
			this.cdr.detectChanges();
		});

		this.initTableConfig();
	}

	ngAfterViewInit(): void {
		this.initParams();
	}

	ngOnDestroy(): void {
		if (this.onlineStatusSub) {
			this.onlineStatusSub.unsubscribe();
		}

		if (this.pilotsSub) {
			this.pilotsSub.unsubscribe();
		}
		if (this.routeSub) {
			this.routeSub.unsubscribe();
		}
		if (this.searchTimeout) {
			clearTimeout(this.searchTimeout);
		}
	}

	initTableConfig() {
		this.configuration = { ...DefaultConfig };
		this.configuration.searchEnabled = false;
		this.configuration.tableLayout.style = "bigger";
		this.configuration.paginationMaxSize = this.constant.PAGINATION.MAX_PAGINATION_COUNT;

		this.columns = [
			{
				key: "staffNumber",
				title: "Staff Id",
				cellTemplate: this.idTpl,
				width: "20%",
			},
			{
				key: "name",
				title: "Name",
				cellTemplate: this.nameTpl,
				width: "40%",
			},
			{
				key: "lastUpdated",
				title: "Last Updated",
				cellTemplate: this.uploadedDateTpl,
				width: "30%",
			},
			{
				key: "action",
				title: "Action",
				cellTemplate: this.actionTpl,
				width: "10%",
			},
		];
	}

	initParams(): void {
		this.routeSub = this.route.queryParams.subscribe((params) => {
			this.updateFilters({
				...this.filters,
				...params,
				startDate: undefined,
				endDate: undefined,
			});
		});
	}

	initPilotDataList(): void {
		if (this.pilotsSub) {
			this.pilotsSub.unsubscribe();
		}

		this.loaderService.setLoading(true);

		this.pilotsSub = this.httpService.pilots(this.filters).subscribe(
			(result) => {
				const data = result.data;
				const pilots = data.data as PilotData[];
				const total: number = data.total;

				this.pilots = pilots;

				const paginationDetails = this.calculatePages(total, this.size);

				this.pagination = {
					limit: this.size,
					offset: this.page,
					count: this.size * paginationDetails.totalPages,
				};

				this.loaderService.setLoading(false);
			},
			(error) => {
				console.error("Error loading pilot data:", error);
				this.loaderService.setLoading(false);
			},
		);
	}

	onTableChanged(event: any) {
		const eventName = event.event;

		switch (eventName) {
			case "onPagination":
				const value = event.value;

				this.updateFilters({
					...this.filters,
					page: value.page,
					size: value.limit,
					search: this.searchQuery,
					sortBy: value.sortBy ?? "name",
					sortOrder: value.sortOrder ?? "asc",
				});
				break;

			default:
				break;
		}
	}

	onSearch(): void {
		if (this.searchTimeout) {
			clearTimeout(this.searchTimeout);
		}

		this.searchTimeout = setTimeout(() => {
			const filters: any = {
				...this.filters,
				page: 1,
				size: this.size,
				sortBy: this.filters?.sortBy ?? "name",
				sortOrder: this.filters?.sortOrder ?? "asc",
			};

			// Only search if query is empty or has at least 3 characters
			if (this.searchQuery && this.searchQuery.trim()) {
				if (this.searchQuery.trim().length >= 3) {
					filters.search = this.searchQuery;
				} else {
					// Don't update filters if less than 3 characters
					return;
				}
			} else {
				delete filters.search;
			}

			if (this.selectedFilter && this.selectedFilter.trim()) {
				filters.filter = this.selectedFilter;
			} else {
				delete filters.filter;
			}

			this.updateFilters(filters);
		}, 500);
	}

	onFilterChange(): void {
		const filters: any = {
			...this.filters,
			page: 1,
			size: this.size,
			sortBy: this.filters?.sortBy ?? "name",
			sortOrder: this.filters?.sortOrder ?? "asc",
		};

		if (this.searchQuery && this.searchQuery.trim()) {
			filters.search = this.searchQuery;
		} else {
			delete filters.search;
		}

		if (this.selectedFilter && this.selectedFilter.trim()) {
			filters.filter = this.selectedFilter;
		} else {
			delete filters.filter;
		}

		this.updateFilters(filters);
	}

	updateFilters(filters: PilotDataParams) {
		if (!filters.startDate) delete filters.startDate;
		if (!filters.endDate) delete filters.endDate;
		this.filters = filters;

		if (!isEmpty(filters?.size)) {
			this.configuration.rows = parseInt(`${this.size}`);
		}

		this.updateUrl();
		this.initPilotDataList();
	}

	updateUrl(): void {
		const url = this.router.serializeUrl(
			this.router.createUrlTree([RouteType.PILOT_DATA], {
				queryParams: this.filters,
			}),
		);

		this.location.go(url);
	}

	selectRow(row: PilotData) {
		this.selectedPilot = row;
	}

	uploadData() {
		this.router.navigate([RouteType.PILOT_DATA_UPLOAD]);
	}

	uploadFile(staffNumber: string): void {
		const pilot = this.pilots.find((p) => p.staffNumber === staffNumber);

		if (pilot) {
			this.router.navigate([RouteType.PILOT_DATA_UPLOAD], {
				queryParams: {
					staffNumber: pilot.staffNumber,
					name: pilot.name,
				},
			});
		}
	}

	viewFolders(staffNumber: string): void {
		const pilot = this.pilots.find((p) => p.staffNumber === staffNumber);

		if (pilot) {
			this.router.navigate(["/pilot-data", pilot.staffNumber, "folders"], {
				queryParams: {
					staffNumber: pilot.staffNumber,
					name: pilot.name,
					lastUpdated: pilot.lastUpdated,
				},
			});
		}
	}

	formatDate(dateString: string): string {
		if (!dateString) return "";
		return moment(dateString).format(CONSTANT.DATE_FORMAT.TABLE_DATE_FORMAT);
	}

	get constant() {
		return CONSTANT;
	}

	get page(): number {
		return parseInt(`${this.filters?.page ?? this.constant.PAGINATION.DEFAULT_PAGE}`);
	}

	get size(): number {
		return parseInt(`${this.filters?.size ?? this.constant.PAGINATION.DEFAULT_SIZE}`);
	}
	calculatePages(totalDataCount: number, rowsPerPage: number): PaginationDetails {
		const totalPages = Math.ceil(totalDataCount / rowsPerPage);
		const lastPageSize = totalDataCount % rowsPerPage;

		return { totalPages, lastPageSize };
	}
}
